/* Live done-ness for the build steps, read from the cfg API.
 *
 * - sources:   any source def whose id ≠ src_identity.
 * - workflows: any workflow that is NOT common-scope AND whose id is outside
 *              the kit {wf_sign_in, wf_sign_up, wf_list_roles,
 *              wf_set_role_journeys}.
 * - journeys:  same rule with the kit {sign_in, sign_up, home, role_access}.
 * - access:    any role doc granting a journey id outside that kit (menu ≡
 *              gate, so this IS publishing).
 * - use:       any run with status 'completed'. */
import {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import type {
    DefListItem,
    IdentityDoc,
    RunRecord
} from '../../api/types';

export const KIT_WORKFLOW_IDS = new Set([
    'wf_sign_in',
    'wf_sign_up',
    'wf_list_roles',
    'wf_set_role_journeys'
]);

export const KIT_JOURNEY_IDS = new Set([
    'sign_in',
    'sign_up',
    'home',
    'role_access'
]);

export interface StageStatus {
    /** null = unknown (the cfg API didn't answer). */
    done: boolean | null;
    detail: string;
}

export interface BuildProgress {
    sources: StageStatus;
    workflows: StageStatus;
    journeys: StageStatus;
    access: StageStatus;
    use: StageStatus;
    loading: boolean;
    reload: () => void;
}

const UNKNOWN: StageStatus = {
    done: null,
    detail: 'Could not read the cfg API.'
};

function plural(n: number, word: string): string {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function ownDefs(items: DefListItem[], kit: Set<string>): DefListItem[] {
    return items.filter((it) => it.scope !== 'common' && !kit.has(it.id));
}

function sourcesStatus(items: DefListItem[] | null): StageStatus {
    if (!items) return UNKNOWN;
    const own = items.filter((it) => it.id !== 'src_identity');
    return {
        done: own.length > 0,
        detail: own.length > 0
            ? `${plural(own.length, 'source')} connected beyond src_identity.`
            : 'Only the predefined identity source so far.'
    };
}

function defsStatus(
    items: DefListItem[] | null,
    kit: Set<string>,
    word: string
): StageStatus {
    if (!items) return UNKNOWN;
    const own = ownDefs(items, kit);
    return {
        done: own.length > 0,
        detail: own.length > 0
            ? `${plural(own.length, `own ${word}`)} beyond the birth-kit.`
            : `Only the birth-kit ${word}s so far.`
    };
}

function accessStatus(roles: IdentityDoc[] | null): StageStatus {
    if (!roles) return UNKNOWN;
    const granted = new Set<string>();
    for (const role of roles) {
        const journeys = Array.isArray(role.journeys) ? role.journeys : [];
        for (const j of journeys) {
            if (!KIT_JOURNEY_IDS.has(String(j))) granted.add(String(j));
        }
    }
    return {
        done: granted.size > 0,
        detail: granted.size > 0
            ? `Published: ${Array.from(granted).join(', ')}.`
            : 'Roles still grant only the birth-kit journeys — nothing published yet.'
    };
}

function runsStatus(runs: RunRecord[] | null): StageStatus {
    if (!runs) return UNKNOWN;
    const completed = runs.filter((r) => String(r.status) === 'completed');
    return {
        done: completed.length > 0,
        detail: completed.length > 0
            ? `${plural(completed.length, 'completed run')} so far.`
            : 'No completed journey run yet.'
    };
}

const PENDING: StageStatus = {
    done: null,
    detail: 'Checking…'
};

export function useBuildProgress(): BuildProgress {
    const {
        tenancy
    } = useAppStudio();
    const [loading, setLoading] = useState(true);
    const [stages, setStages] = useState({
        sources: PENDING,
        workflows: PENDING,
        journeys: PENDING,
        access: PENDING,
        use: PENDING
    });

    const reload = useCallback(() => {
        let alive = true;
        setLoading(true);
        const safe = <T>(p: Promise<T>) => p.catch(() => null);
        Promise.all([
            safe(appEngineService.listDefs(tenancy, 'source')),
            safe(appEngineService.listDefs(tenancy, 'workflow')),
            safe(appEngineService.listDefs(tenancy, 'journey')),
            safe(appEngineService.listRoles(tenancy)),
            safe(appEngineService.listRuns(tenancy, {
                status: 'completed'
            }))
        ]).then(([sources, workflows, journeys, roles, runs]) => {
            if (!alive) return;
            setStages({
                sources: sourcesStatus(sources),
                workflows: defsStatus(workflows, KIT_WORKFLOW_IDS, 'workflow'),
                journeys: defsStatus(journeys, KIT_JOURNEY_IDS, 'journey'),
                access: accessStatus(roles),
                use: runsStatus(runs)
            });
            setLoading(false);
        });
        return () => {
            alive = false;
        };
    }, [tenancy]);

    useEffect(() => reload(), [reload]);

    return {
        ...stages,
        loading,
        reload
    };
}
